import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleHeader from '../../components/layout/SimpleHeader';
import SelectCampus from '../../components/SelectCampus';
import Button from '../../components/Button';
import { Empty, Table, TableColumnsType } from 'antd';
import Pagination from '../../components/Pagination';
import ExtendButton from '../../components/ExtendButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import Loader from '../../components/Loader';
import ErrorResponse from '../../components/ErrorResponse';
import BookCopyBasicInformation from './bookCopyManagement/BookCopyBasicInformation';
import styled from 'styled-components';
import { getCommonCodeLabel } from '../../lib/store';
import { useGetCopyBooksQuery } from '../../api/books/bookManagement.api';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import Split from 'react-split';
import TableEmpty from '../../components/common/TableEmpty';

const Block = styled.div`
  .close-left {
    transform: translate(-8.3333%);
  }

  .table-inside {
    width: 100%;

    th {
      background: ghostwhite;
    }
  }

  .img-preview {
    max-width: 150px;
  }
`;

const CopyBooks: FC = () => {
  const { t, i18n } = useTranslation();
  const keywordRef = useRef<any>();
  const [isExpand, setIsExpand] = useState(false);
  const { currentCampus: campusCode } = useSelector(
    (state: RootState) => state.auth,
  );
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [page, setPage] = useState(1);
  const barcode = useBarcodeScanner();
  const [searchData, setSearchData] = useState<any>({
    campusCode,
    keyword: '',
    pageSize: 15,
    page: 1,
  });

  const {
    data: copyBooks,
    isFetching,
    isLoading,
    isSuccess,
    error,
  } = useGetCopyBooksQuery(searchData);

  const columns: TableColumnsType<any> = [
    {
      title: t('booksManagement.books.column1'),
      dataIndex: 'id',
      align: 'center',
      width: 50,
      render: (item, record, index) => (
        <>{(page - 1) * searchData.pageSize + index + 1}</>
      ),
    },
    {
      title: t('booksManagement.books.column2'),
      dataIndex: 'bookName',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.bookName.localeCompare(b.bookName),
      render: value => {
        return (<div className='text-left ant-table-cell-ellipsis'>{value}</div>);
      }
    },
    {
      title: t('booksManagement.books.column4'),
      dataIndex: 'isbn',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.isbn.localeCompare(b.isbn),
    },
    {
      title: t('booksManagement.books.column19'),
      dataIndex: 'bookSeq',
      width: 100,
      align: 'center',
      render: (value) => (value ? value : '-'),
      sorter: (a, b) => a.bookSeq - b.bookSeq,
    },
    {
      title: t('booksManagement.books.column31'),
      dataIndex: 'bookStatus',
      width: 100,
      align: 'center',
      render: (value: string) =>
        value ? getCommonCodeLabel('BK0002', value, i18n.language) : '-',
      sorter: (a, b) => {
        if (!a?.bookStatus || !b?.bookStatus) return;

        return getCommonCodeLabel(
          'BK0002',
          a.bookStatus,
          i18n.language,
        ).localeCompare(
          getCommonCodeLabel('BK0002', b.bookStatus, i18n.language),
        );
      },
    },
    {
      title: t('booksManagement.books.column36'),
      dataIndex: 'memberName',
      align: 'center',
      ellipsis: true,
      render: (value) => (value ? value : '-'),
      sorter: (a, b) => {
        if (!a?.memberName || !b?.memberName) return;

        return a?.memberName.localeCompare(b?.memberName);
      },
    },
    {
      title: t('booksManagement.books.column37'),
      dataIndex: 'overdueDay',
      width: 100,
      align: 'center',
      render: (value) => (value ? `${value}${t('books.text.21')}` : '-'),
      sorter: (a, b) => a?.overdueDay - b?.overdueDay,
    },
  ];

  useEffect(() => {
    if (!keywordRef) return;

    keywordRef.current.focus();
  }, [keywordRef]);

  useEffect(() => {
    if (campusCode) {
      setSearchData({
        ...searchData,
        campusCode,
      });
    }
    setSelectedRow(null);
  }, [campusCode]);

  useEffect(() => {
    if (!searchData) return;

    setSearchData({ ...searchData, page });
  }, [page]);

  useEffect(() => {
    if (isFetching || !isSuccess || !selectedRow) return;

    setSelectedRow(
      copyBooks?.data?.copyBooksDtos?.find((book: any) => {
        if (selectedRow?.bookSeq) {
          return (
            book?.isbn === selectedRow?.isbn &&
            book?.bookSeq === selectedRow?.bookSeq
          );
        }

        return book?.isbn === selectedRow?.isbn;
      }),
    );
  }, [isFetching, isSuccess]);

  useEffect(() => {
    if (barcode !== null && keywordRef) {
      setSearchData({ ...searchData, keyword: barcode });
      keywordRef.current.value = barcode;
    }
  }, [barcode]);

  return (
    <>
    <SimpleHeader
        iconFile="icon-book-big.png"
        parentMenu={t('common.menu.book')}
        currentMenu={t('booksManagement.books.column38')}
        onlyTitle={true}
      />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('booksManagement.books.column25')}</label>
            <div>
              <input
                type="text"
                ref={keywordRef}
                placeholder={t('booksManagement.books.column25')}
                className="form-control"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setSearchData({
                      ...searchData,
                      keyword: keywordRef.current.value,
                      page: 1,
                    });
                  }
                }}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={() =>
                  setSearchData({
                    ...searchData,
                    keyword: keywordRef.current.value,
                    page: 1,
                  })
                }
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <Block className="content">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <Split
        className="splite-divide"
        sizes={[60, 40]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
      >

        <div className="col-frame left_panel">
          <div className="comm-bx__between">
            <div className="comm-sch_result">
              {t('common.text.count')}: <b>{copyBooks?.data?.totalCount ?? 0}</b>
              {t('books.text.6')}
            </div>
          </div>
          <div className="comm-table__area">
          {copyBooks?.data?.copyBooksDtos ? (
            <Table
              columns={columns}
              dataSource={copyBooks?.data?.copyBooksDtos ?? []}
              sticky={true}
              rowKey={(record: any) => record?.no}
              loading={isFetching}
              pagination={{
                current: searchData.page,
                pageSize: searchData.pageSize,
              }}
              onRow={(record) => {
                return { onClick: () => setSelectedRow(record) };
              }}
              rowClassName={(record: any) =>
                record?.no === selectedRow?.no ? 'chosen' : 'pointer'
              }
            />
          ) : (
            <>
              <TableEmpty text={t('shuttle.text.40')} />
            </>
          )}
          </div>
          <div className="comm-bx__between">
            <div className="common-paging center">
              <Pagination
                count={copyBooks?.data?.totalCount || 0}
                page={searchData!.page}
                perPage={searchData!.pageSize}
                param={searchData}
                setPage={setPage}
                small
              />
            </div>
          </div>
        </div>
        
        <div className="col-frame right_panel"> 
          <div className="w-100">
            <div className="comm-bx__between">
              <h3 className='sub-title'>{t('books.text.8')}</h3>
            </div>
            {selectedRow ? (
              <BookCopyBasicInformation
                bookNo={selectedRow?.bookNo}
                bookSeq={selectedRow?.bookSeq}
                campusCode={campusCode}
              />
            ) : (
              <>
                <div className='comm-table__area'>
                  <TableEmpty text={t('shuttle.text.40')} />
                </div>
              </>
            )}
          </div>
        </div>
      </Split>
    </Block>
    </>
  );
};
export default CopyBooks;
